import React, { useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import useNotificationStore, { Notification } from '../../stores/notificationStore';
import { NOTIFICATION_TYPES } from '../../utils/constants';

const ToastItem: React.FC<{
  notification: Notification;
  onClose: () => void;
}> = ({ notification, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  const getIcon = () => {
    const iconClass = 'h-5 w-5 flex-shrink-0';
    
    switch (notification.type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return <CheckCircleIcon className={`${iconClass} text-green-500`} />;
      case NOTIFICATION_TYPES.ERROR:
        return <XCircleIcon className={`${iconClass} text-red-500`} />;
      case NOTIFICATION_TYPES.WARNING:
        return <ExclamationCircleIcon className={`${iconClass} text-yellow-500`} />;
      case NOTIFICATION_TYPES.INFO:
      default:
        return <InformationCircleIcon className={`${iconClass} text-blue-500`} />;
    }
  };

  const getBackgroundColor = () => {
    switch (notification.type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return 'bg-green-50 border-green-200';
      case NOTIFICATION_TYPES.ERROR:
        return 'bg-red-50 border-red-200';
      case NOTIFICATION_TYPES.WARNING:
        return 'bg-yellow-50 border-yellow-200';
      case NOTIFICATION_TYPES.INFO:
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Aguardar animação de saída
  };

  return (
    <Transition
      show={isVisible}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-x-full opacity-0"
      enterTo="translate-x-0 opacity-100"
      leave="transform ease-in duration-300 transition"
      leaveFrom="translate-x-0 opacity-100"
      leaveTo="translate-x-full opacity-0"
    >
      <div
        className={`
          min-w-[320px] max-w-md w-full pointer-events-auto
          rounded-lg border shadow-lg
          ${getBackgroundColor()}
        `}
      >
        <div className="p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">{getIcon()}</div>
            <div className="ml-3 w-0 flex-1">
              {notification.title && (
                <p className="text-sm font-medium text-gray-900">
                  {notification.title}
                </p>
              )}
              <p className="mt-1 text-sm text-gray-600">
                {notification.message}
              </p>
              {notification.action && (
                <div className="mt-3">
                  <button
                    onClick={notification.action.onClick}
                    className="text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none"
                  >
                    {notification.action.label}
                  </button>
                </div>
              )}
            </div>
            <div className="ml-4 flex-shrink-0 flex">
              <button
                className="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
                onClick={handleClose}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

const ToastContainer: React.FC = () => {
  const { notifications, removeNotification, position } = useNotificationStore();

  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'top-center':
        return 'top-4 left-1/2 transform -translate-x-1/2';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'bottom-center':
        return 'bottom-4 left-1/2 transform -translate-x-1/2';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'top-right':
      default:
        return 'top-4 right-4';
    }
  };

  return (
    <div
      className={`
        fixed z-[9999] pointer-events-none
        ${getPositionClasses()}
      `}
      style={{ maxWidth: 'calc(100vw - 2rem)' }}
      aria-live="assertive"
    >
      <div className="space-y-4">
        {notifications.map((notification) => (
          <ToastItem
            key={notification.id}
            notification={notification}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default ToastContainer;